import React, { useState, useCallback, useEffect, Fragment } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { 
	makeStyles,
	Button,
	Popover,
	Grid,
	IconButton,
	Typography,
	FormControl,
	InputLabel,
	ButtonGroup,
	FormHelperText
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';

import Calendar from './Calendar';
import Select from '../Select';
import { maxWidth } from '../configs';
import utils from './utils';

const periodFormat = 'YYYY-MM';
const dateFormat = 'YYYY-MM-DD';
const dateTimeFormat = 'YYYY-MM-DD HH:mm';
const timeFormat = 'HH:mm';
const defaultDateTimeDisplayFormat = 'D MMM YYYY HH:mm';
const defaultDateDisplayFormat = 'D MMM YYYY';
const defaultTimeInterval = 30;

const yearOffset = 5;
const yearOptionLength = 100;
const startingYear = moment().year() - (yearOptionLength - yearOffset);

const yearOptions = Array(yearOptionLength).fill('').map((e, i) => ({
	label: String(startingYear + i),
	value: String(startingYear + i),
}));

const monthOptions = moment.monthsShort().map((e, i) => ({
	label: e,
	value: String(i),
}));

const emptyFunction = () => {};

const generateOutputValue = (rawValue, showTime) => {
	if(showTime) return rawValue;
	return moment(rawValue).format(dateFormat);
};

const useStyles = makeStyles(theme => ({
	InputLabel: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		width: 'calc(100% - 14px)',
	},
	InputLabelShrink: {
		width: '107%'
	},
	baseMargin: {
		width: '100%',
		maxWidth,
		marginTop: 8,
		marginBottom: 8,
	},
	baseFullWidth: {
		width: 'calc(100% - 10px)',
	},
	button: {
		fontSize: '1rem',
		paddingTop: 8,
		paddingBottom: 8,
		border: `1px solid ${theme.palette.border.primary}`,
		backgroundColor: theme.palette.grey.primary,
		minWidth: 108,
	},
	paper: {
		width: 'auto',
		boxShadow: 'none',
		border: '1px solid #e6e6e6',
	},
	wrapper: {
		padding: 5,
	},
	container: {
		outline: 'none',
	},
	actionWrapper: {
		display: 'flex',
		justifyContent: 'flex'
	},
	withLabel: {
		paddingLeft: 12,
		paddingRight: 12,
		paddingTop: 16,
		paddingBottom: 0,
		minHeight: 48,
	},
	buttonLabel: {
		justifyContent: 'flex-start'
	},
	grow: {
		flex: 1
	},
	clearButton: {
		fontSize: '1rem',
		paddingTop: 8,
		paddingBottom: 8,
		border: `1px solid ${theme.palette.border.primary}`,
		borderLeft: 'none',
		backgroundColor: theme.palette.grey.primary,
		borderRadius: theme.shape.borderRadius,
	},
	timeSelect: {
		margin: 10,
		maxWidth: 'calc(100% - 20px)',
		'& li': {
			justifyContent: 'center',
		}
	},
	periodSelect: {
		margin: 0,
		width: 'auto',
		'& div': {
			backgroundColor: 'white'
		}
	}
}));

function DateTimePicker(props) {
	// props
	const { 
		label, 
		PopoverProps = {},
		ButtonProps = {},
		timeInterval = defaultTimeInterval,
		dateDisplayFormat = defaultDateDisplayFormat,
		dateTimeDisplayFormat = defaultDateTimeDisplayFormat,
		isDayBlocked = emptyFunction,
		isDayHighlighted = emptyFunction,
		onChange = emptyFunction,
		onClose = emptyFunction,
		onNextMonthClick = emptyFunction,
		onPrevMonthClick = emptyFunction,
		hideSelectedDateLabel,
		hideDefaultValue,
		blockPassedDay,
		autoApply,
		highlightClassName,
		rootClassName,
		clearable,
		helperText,
		highlightDate,
		fullWidth,
		showTime,
		...others
	} = props;

	const timeOptions = utils.generateTimeOptions(timeInterval);
	const value = utils.generateDefaultValue(props.value, timeOptions, showTime);

	const classes = useStyles();
	const [anchorEl, setAnchorEl] = useState(null);
	const [dateTime, setDateTime] = useState(moment(value).format(dateTimeFormat));
	const [period, setPeriod] = useState(moment(value).format(periodFormat));

	useEffect(() => {
		if(!anchorEl) return;

		setDateTime(moment(value).format(dateTimeFormat));

	}, [Boolean(anchorEl), value]);

	// popover controller
	const handleOpen = useCallback(e => setAnchorEl(e.currentTarget), []);
	const handleClose = useCallback(() => {
		onClose({ date: props.value });
		setAnchorEl(null);
	}, [props.value, onClose]);

	// navigate month
	const handlePrevMonthClick = useCallback(() => {
		setPeriod((prevPeriod) => {
			const newPeriod = moment(prevPeriod, periodFormat).add(-1, 'month');
			onPrevMonthClick(newPeriod.format(dateFormat));
			return newPeriod.format(periodFormat);
		});
	}, [onPrevMonthClick]);

	const handleNextMonthClick = useCallback(() => {
		setPeriod((prevPeriod) => {
			const newPeriod = moment(prevPeriod, periodFormat).add(1, 'month');
			onNextMonthClick(newPeriod.format(dateFormat));
			return newPeriod.format(periodFormat);
		});
	}, [onNextMonthClick]);

	const handleSetPeriod = (type) => (event, value) => {
		setPeriod((prevPeriod) => {
			const newPeriod = moment(prevPeriod, periodFormat)[type](value);
			return newPeriod.format(periodFormat);
		});
	};

	const handleSetDate = useCallback((newDate) => {
		setDateTime((prevDateTime) => {
			const newMoment = moment(newDate);
			return moment(prevDateTime)
				.year(newMoment.year())
				.month(newMoment.month())
				.date(newMoment.date())
				.format(dateTimeFormat)
		});
	}, []);

	const handleSetTime = useCallback((newTime) => {
		setDateTime((prevDateTime) => {
			const newMoment = moment(newTime, timeFormat);
			return moment(prevDateTime)
				.hour(newMoment.hour())
				.minute(newMoment.minute())
				.format(dateTimeFormat)
		});
	}, []);

	const handleTimeChange = (event) => {
		const newValue = event.target.value;
		handleSetTime(newValue);
	};

	// handle apply
	const handleApply = useCallback((event) => {
		onClose({ date: dateTime });
		onChange(event, generateOutputValue(dateTime, showTime));
		setAnchorEl(null);
	}, [dateTime, onClose, onChange, showTime]);

	const handleDateClick = useCallback((selectedDate) => {
		if(!selectedDate) return;
		handleSetDate(moment(selectedDate).format(dateFormat));
		if(autoApply) {
			onChange(null, generateOutputValue(selectedDate, showTime));
			setAnchorEl(null);
		}
	}, [autoApply, onChange, showTime, handleSetDate]);

	const handleSetToday = useCallback(() => {
		setPeriod(moment().format(periodFormat));
		handleSetDate(moment().format(dateFormat));
	}, [handleSetDate]);

	const handleOnEntering = useCallback(() => {
		// reset data on entering
		handleSetDate(value);
		setPeriod(moment(value).format(periodFormat));
	}, [value, handleSetDate]);

	const handleClear = useCallback((event) => {
		onChange(event, null);
	}, [onChange]);

	const renderButtonText = useCallback((value) => {
		const dateMoment = moment(value);
		const isValidDate = dateMoment.isValid();
		if(!value && hideDefaultValue) return '';
		if(!isValidDate) return '';
		else return dateMoment.format(
			showTime
				? dateTimeDisplayFormat
				: dateDisplayFormat
		);
	}, [dateDisplayFormat, dateTimeDisplayFormat, hideDefaultValue, showTime]);

	return (
		<Fragment>
			{Boolean(label) && (
				<FormControl className={clsx(
					classes.baseMargin,
					fullWidth && classes.baseFullWidth,
				)} {...others}>
					<InputLabel
					 classes={{
					 	root: classes.InputLabel,
					 	shrink: classes.InputLabelShrink
					 }}
					 shrink={Boolean(props.value || anchorEl)}
					 focused={Boolean(anchorEl)}
					>
						{label}
					</InputLabel>
					<ButtonGroup aria-label="split button">
						<Button
							className={clsx(
								classes.grow,
								classes.button, 
								classes.withLabel, 
								rootClassName, 
								ButtonProps.className
							)}
							classes={{ label: classes.buttonLabel }}
							aria-controls="date-menu" 
							aria-haspopup="true" 
							{...ButtonProps}
							 onClick={handleOpen}
						>
							{renderButtonText(props.value)}
						</Button>
						{clearable && (
							<IconButton 
								className={classes.clearButton} 
								onClick={handleClear} 
								aria-label="clear"
							>
								<ClearIcon />
							</IconButton>
						)}
					</ButtonGroup>
					<FormHelperText>{helperText}</FormHelperText>
				</FormControl>
			)}
			{!Boolean(label) && (
				<Button
					className={clsx(
						classes.button, 
						classes.baseMargin, 
						fullWidth && classes.baseFullWidth,
						rootClassName, 
						ButtonProps.className
					)}
					classes={{ label: classes.buttonLabel }}
					aria-controls="date-menu" 
					aria-haspopup="true" 
					fullWidth={fullWidth}
					{...ButtonProps}
					onClick={handleOpen}
				>
					{renderButtonText(props.value)}
				</Button>
			)}
			<Popover
				classes={{ paper: classes.paper }}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				onEntering={handleOnEntering}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				{...PopoverProps}
			>
				<Grid className={classes.wrapper} container direction="column" >
					{!hideSelectedDateLabel && (
						<Grid item xs={12}>
							<Typography align="center">
								{renderButtonText(dateTime)}
							</Typography>
						</Grid>
					)}
					<Grid className={classes.container} item xs={12}>
						<Grid container justify="space-between" alignItems="center">
							<IconButton size="small" onClick={handlePrevMonthClick}>
								<KeyboardArrowLeft />
							</IconButton>
							<div style={{ display: 'flex' }}>
								<Select 
									className={classes.periodSelect} 
									onChange={handleSetPeriod('year')}
									maxWidth={100} 
									options={yearOptions} 
									value={String(moment(period).year())}
									disableNative
								/>
								<Select 
									className={classes.periodSelect} 
									onChange={handleSetPeriod('month')}
									maxWidth={100} 
									options={monthOptions} 
									value={String(moment(period).month())}
									disableNative
								/>
							</div>
							<IconButton size="small" onClick={handleNextMonthClick}>
								<KeyboardArrowRight />
							</IconButton>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<Grid container direction="column">
							<Calendar 
								date={moment(dateTime).format(dateFormat)}
								period={period}
								onDateClick={handleDateClick}
								isDayBlocked={isDayBlocked}
								isDayHighlighted={isDayHighlighted}
								blockPassedDay={blockPassedDay}
								highlightClassName={highlightClassName}
							/>
							{showTime && (
								<Select 
									label="Time"
									className={classes.timeSelect} 
									options={timeOptions} 
									onChange={handleTimeChange}
									value={moment(dateTime).format(timeFormat)}
									disableNative
								/>
							)}
						</Grid>
					</Grid>
					{!autoApply && (
						<Grid item xs={12}>
							<Grid container justify="space-between">
								<div>
									<Button color="primary" onClick={handleSetToday}>Today</Button>
								</div>
								<div>
									<Button color="primary" onClick={handleClose}>Cancel</Button>
									<Button color="primary" onClick={handleApply}>Apply</Button>
								</div>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Popover>
		</Fragment>
	);
}

export default DateTimePicker;
